import React, { useEffect, useMemo, useRef, useState } from 'react';

import { FormTitle } from '@components/admin/Forms';
import { ChecklistDisplay } from '@components/admin/members/ChecklistDisplay';
import { CompanyDisplay } from '@components/admin/members/CompanyDisplay';
import {
  MembershipTextInput,
  RadioGroupInput,
} from '@components/admin/members/MembershipFormDetails/Forms';
import { PrincipalDisplay } from '@components/admin/members/PrincipalDisplay';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { SpinnerModal } from '@components/SpinnerModal';
import { GoodsInput, YesNoInput } from '@components/utilities/MembershipForm/Form';
import { strings as membershipFormStrings } from '@data/membershipForm';
import { usePatchCompany } from '@hooks/mutation/dashboard/usePatchCompany';
import { usePatchCompanyChecklist } from '@hooks/mutation/dashboard/usePatchCompanyChecklist';
import { usePatchCompanyPrincipal } from '@hooks/mutation/dashboard/usePatchCompanyPrincipal';
import { useSearch } from '@hooks/useSearch';
import {
  ChecklistForm,
  CompanyForm,
  MembershipFormFields,
  PrincipalForm,
} from '@models/MembershipForm';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { createValidate, formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { isRight } from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import { Prompt } from 'react-router-dom';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    save: '保存',
    companyInfo: `客側${membershipFormStrings.companyInfo}`,
    principalInfo: `客側${membershipFormStrings.principalInfo}`,
    checklist: membershipFormStrings.checklist,
    defaultTransactionConditions: '普通',
    proposedTransactionConditions: '相談したい',
    fixError:
      '正しく入力されていない項目があります。エラーメッセージをご確認の上、もう一度ご入力ください。',
  },
});

const useStyles = makeMuiStyles((_, common) => ({
  formContainer: {
    ...common.flexColumn,
    marginBottom: '1rem',
  },
  formButton: {
    width: 70,
    margin: '1.5rem 1rem',
  },
  createButtonContainer: {
    ...common.flexInline,
    width: '100%',
    justifyContent: 'flex-end',
  },
}));

const searchType = t.type({
  id: h.number().cast(),
  creating: h.omittable(h.boolean().cast()),
});

interface CompleteMemberFormProps {
  initialValues: PrincipalForm & CompanyForm & ChecklistForm;
  onFinish: () => void;
}

export const CompleteMemberForm: React.FC<CompleteMemberFormProps> = (props) => {
  const { onFinish, initialValues } = props;

  const [edittingCompanyInfo, setEdittingCompanyInfo] = useState(false);
  const [edittingPrincipalInfo, setEdittingPrincipalInfo] = useState(false);
  const [edittingChecklist, setEdittingChecklist] = useState(false);
  const [fixErrorOpen, setFixErrorOpen] = useState(false);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const actionRef = useRef<'company' | 'principal' | 'checklist' | 'all'>('all');

  const principalSchema = useMemo(
    () => MembershipFormFields.shared.toPrincipalFormSchemaLoose(),
    [],
  );
  const checklistSchema = useMemo(() => MembershipFormFields.shared.toChecklistFormSchema(), []);
  const companySchema = useMemo(() => MembershipFormFields.shared.toCompanyJPFormSchemaLoose(), []);

  const { search, updateSearch } = useSearch(searchType);
  const isCreating = !!search.creating;

  const validate = useMemo(
    () => (values: any) => ({
      ...createValidate(companySchema)(values),
      ...createValidate(principalSchema)(values),
      ...createValidate(checklistSchema)(values),
    }),
    [companySchema, principalSchema, checklistSchema, setFixErrorOpen, isCreating],
  );

  const patchCompany = usePatchCompany(search.id);
  const patchPrincipal = usePatchCompanyPrincipal(search.id);
  const patchChecklist = usePatchCompanyChecklist(search.id);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  useEffect(() => {
    const companyValidation = companySchema.decode(initialValues);
    const principalValidation = principalSchema.decode(initialValues);
    const checklistValidation = checklistSchema.decode(initialValues);

    const dataIsComplete =
      isRight(companyValidation) && isRight(principalValidation) && isRight(checklistValidation);

    if (!dataIsComplete) updateSearch({ ...search, creating: true });
  }, []);

  return (
    <Formik
      validate={validate}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values) => {
        const submitCompany = ['company', 'all'].includes(actionRef.current);
        const submitPrincipal = ['principal', 'all'].includes(actionRef.current);
        const submitChecklist = ['checklist', 'all'].includes(actionRef.current);

        const companyValidation = companySchema.decode(values);
        const principalValidation = principalSchema.decode(values);
        const checklistValidation = checklistSchema.decode(values);

        if (
          (!isRight(companyValidation) && submitCompany) ||
          (!isRight(principalValidation) && submitPrincipal) ||
          (!isRight(checklistValidation) && submitChecklist)
        ) {
          throw new Error('Formik: Unexpected erroneous form values. ');
        }

        try {
          if (isRight(companyValidation) && submitCompany) {
            const patchCompanyData = MembershipFormFields.shared.mapCompanyFormLooseToCompany(
              companyValidation.right,
            );
            await patchCompany.mutateAsync(patchCompanyData);
          }
          if (isRight(principalValidation) && submitPrincipal) {
            const patchPrincipalData = MembershipFormFields.shared.mapPrincipalFormToPatchPrincipal(
              principalValidation.right,
            );
            await patchPrincipal.mutateAsync(patchPrincipalData);
          }
          if (isRight(checklistValidation) && submitChecklist) {
            const patchChecklistData = checklistValidation.right;
            await patchChecklist.mutateAsync(patchChecklistData);
          }
          onFinish();
          if (submitCompany) {
            setEdittingCompanyInfo(false);
          }
          if (submitPrincipal) {
            setEdittingPrincipalInfo(false);
          }
          if (submitChecklist) {
            setEdittingChecklist(false);
          }
        } catch (e) {
          if (e instanceof FormError) {
            // eslint-disable-next-line no-alert
            alert(e.getDisplayMessage());
          }
          throw e;
        }
      }}
    >
      {({ dirty, isSubmitting, values }) => (
        <Form className={clsx(classes.formContainer, classes.verticalSpace1)}>
          <Prompt when={dirty && !isSubmitting} message={() => formStrings.promptLeaving} />
          <Alert
            open={fixErrorOpen}
            onClose={() => setFixErrorOpen(false)}
            severity="error"
            scrollOnOpen={true}
            scrollTrigger={scrollTrigger}
          >
            {strings.fixError}
          </Alert>

          <SpinnerModal
            open={patchPrincipal.isLoading || patchChecklist.isLoading || patchCompany.isLoading}
          />

          <FormTitle
            title={strings.companyInfo}
            noEdit={isCreating}
            editting={edittingCompanyInfo}
            onChangeEditting={setEdittingCompanyInfo}
          />
          {edittingCompanyInfo || isCreating ? (
            <>
              <MembershipTextInput required field={MembershipFormFields.shared.companyName} />
              <MembershipTextInput required field={MembershipFormFields.shared.englishName} />
              <MembershipTextInput required field={MembershipFormFields.shared.corporateNumber} />
              <MembershipTextInput field={MembershipFormFields.shared.zipcodeLoose} />
              <MembershipTextInput required field={MembershipFormFields.shared.address1} />
              <MembershipTextInput field={MembershipFormFields.shared.address2Loose} />
              <MembershipTextInput required field={MembershipFormFields.shared.phone} />
              <MembershipTextInput field={MembershipFormFields.shared.fax} />
              <MembershipTextInput field={MembershipFormFields.shared.startDateLoose} />
              <MembershipTextInput field={MembershipFormFields.shared.representerLastName} />
              <MembershipTextInput field={MembershipFormFields.shared.representerFirstName} />
              <MembershipTextInput field={MembershipFormFields.shared.legalCapital} />
              <MembershipTextInput field={MembershipFormFields.shared.industry} />
              <MembershipTextInput field={MembershipFormFields.shared.employeeCountLoose} />
              <RadioGroupInput
                name={MembershipFormFields.shared.transactionCondition.name}
                label={MembershipFormFields.shared.transactionCondition.label}
                options={[
                  {
                    label: strings.defaultTransactionConditions,
                    value: 'Y',
                  },
                  {
                    label: strings.proposedTransactionConditions,
                    value: 'N',
                  },
                ]}
              />
              <MembershipTextInput field={MembershipFormFields.shared.transactionConditionDetail} />
              {!isCreating && (
                <Button
                  color="primary"
                  shape="round"
                  className={classes.formButton}
                  text={strings.save}
                  onClick={() => (actionRef.current = 'company')}
                />
              )}
            </>
          ) : (
            <CompanyDisplay values={initialValues} />
          )}

          <FormTitle
            title={strings.principalInfo}
            noEdit={isCreating}
            editting={edittingPrincipalInfo}
            onChangeEditting={setEdittingPrincipalInfo}
          />
          {edittingPrincipalInfo || isCreating ? (
            <>
              <MembershipTextInput required field={MembershipFormFields.shared.principalLastName} />
              <MembershipTextInput field={MembershipFormFields.shared.principalFirstName} />
              <MembershipTextInput field={MembershipFormFields.shared.principalCellphone} />
              <MembershipTextInput field={MembershipFormFields.shared.principalLandline} />
              <MembershipTextInput field={MembershipFormFields.shared.principalEmail} disabled />
              {!isCreating && (
                <Button
                  color="primary"
                  shape="round"
                  className={classes.formButton}
                  text={strings.save}
                  onClick={() => (actionRef.current = 'principal')}
                />
              )}
            </>
          ) : (
            <PrincipalDisplay values={initialValues} />
          )}

          <FormTitle
            title={strings.checklist}
            noEdit={isCreating}
            editting={edittingChecklist}
            onChangeEditting={setEdittingChecklist}
          />
          {edittingChecklist || isCreating ? (
            <>
              <YesNoInput field={MembershipFormFields.shared.question1} />
              <YesNoInput field={MembershipFormFields.shared.question2} />
              <YesNoInput field={MembershipFormFields.shared.question3} />
              <YesNoInput field={MembershipFormFields.shared.question4} />
              <YesNoInput field={MembershipFormFields.shared.question5} />
              <YesNoInput field={MembershipFormFields.shared.question6} />
              <YesNoInput field={MembershipFormFields.shared.question7} />
              <GoodsInput field={MembershipFormFields.shared.question8} type="export" />
              <GoodsInput field={MembershipFormFields.shared.question9} type="import" />
              <YesNoInput field={MembershipFormFields.shared.question10} />
              <YesNoInput field={MembershipFormFields.shared.question11} />
              {!isCreating && (
                <Button
                  color="primary"
                  shape="round"
                  className={classes.formButton}
                  text={strings.save}
                  onClick={() => (actionRef.current = 'checklist')}
                />
              )}
            </>
          ) : (
            <ChecklistDisplay values={initialValues} />
          )}

          {isCreating && (
            <div className={classes.createButtonContainer}>
              <Button
                color="primary"
                shape="round"
                className={classes.formButton}
                text={strings.save}
                onClick={() => {
                  actionRef.current = 'all';
                  const errors = {
                    ...createValidate(companySchema)(values),
                    ...createValidate(principalSchema)(values),
                    ...createValidate(checklistSchema)(values),
                  };
                  if (Object.keys(errors).length > 0 && isCreating) {
                    setScrollTrigger((prev) => prev + 1);
                    setFixErrorOpen(true);
                  }
                }}
              />
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};
